/**
 * ExtractedDocumentsPanel - Display AI-extracted supporting document data
 * =========================================================================
 * 
 * Shows extracted terms from lease agreements, insurance policies, and loan documents.
 * Used in case detail pages to display key financial terms.
 * 
 * P2: Case Pipeline - Lease/Contract Document Parsing
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import {
  FileText,
  Home,
  Shield,
  Landmark,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  DollarSign,
  Calendar,
  Percent,
  Eye,
  AlertCircle,
  Edit
} from 'lucide-react';

// Types
interface LeaseTerms {
  monthly_rent?: number;
  lease_start_date?: string;
  lease_end_date?: string;
  lease_term_months?: number;
  property_address?: string;
  landlord_name?: string;
  escalation?: {
    type: string;
    rate?: number;
    frequency: string;
  };
  cam_charges?: number;
  square_footage?: number;
}

interface InsuranceTerms {
  policy_number?: string;
  carrier_name?: string;
  bi_coverage_limit?: number;
  bi_waiting_period_days?: number;
  bi_period_of_indemnity_months?: number;
  bi_deductible?: number;
  policy_effective_date?: string;
  policy_expiration_date?: string;
  exclusions?: string[];
}

interface LoanTerms {
  monthly_payment?: number;
  interest_rate?: number;
  original_principal?: number;
  current_balance?: number;
  loan_maturity_date?: string;
  lender_name?: string;
  loan_type?: string;
  is_sba_loan?: boolean;
  ppp_loan?: boolean;
  eidl_loan?: boolean;
}

interface ExtractedDocument {
  document_id: string;
  case_id: string;
  document_type: 'lease_agreement' | 'insurance_policy' | 'loan_document' | 'contract';
  confidence: 'high' | 'medium' | 'low' | 'manual';
  confidence_score: number;
  needs_review: boolean;
  extracted_at: string;
  original_filename?: string;
  lease_terms?: LeaseTerms;
  insurance_terms?: InsuranceTerms;
  loan_terms?: LoanTerms;
  extraction_warnings?: string[];
  verified_by?: string;
  verified_at?: string;
}

interface DocumentSummary {
  case_id: string;
  total_documents: number;
  extracted_documents: number;
  pending_extraction: number;
  needs_review: number;
  by_type: Record<string, number>;
  documents: ExtractedDocument[];
}

interface ExtractedDocumentsPanelProps {
  caseId: string;
  compact?: boolean;
  onExtract?: () => void;
}

// Utility functions
const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return 'N/A';
  // If stored as decimal (e.g., 0.065), convert to percentage
  const pct = value < 1 ? value * 100 : value;
  return `${pct.toFixed(2)}%`;
};

// Document type icons and colors
const DOC_CONFIG = {
  lease_agreement: {
    icon: Home,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    label: 'Lease Agreement'
  },
  insurance_policy: {
    icon: Shield,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    label: 'Insurance Policy'
  },
  loan_document: {
    icon: Landmark,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    label: 'Loan Document'
  },
  contract: {
    icon: FileText,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    label: 'Contract'
  }
};

// Confidence badge
const ConfidenceBadge: React.FC<{ confidence: string; score: number }> = ({ confidence, score }) => {
  const config = {
    high: { color: 'text-emerald-700 bg-emerald-100', label: 'High' },
    medium: { color: 'text-amber-700 bg-amber-100', label: 'Medium' },
    low: { color: 'text-red-700 bg-red-100', label: 'Low' },
    manual: { color: 'text-blue-700 bg-blue-100', label: 'Verified' }
  };
  const cfg = config[confidence as keyof typeof config] || config.medium;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      {confidence === 'manual' ? <CheckCircle className="w-3 h-3" /> : null}
      {cfg.label} ({Math.round(score * 100)}%)
    </span>
  );
};

// Lease Terms Display
const LeaseTermsDisplay: React.FC<{ terms: LeaseTerms }> = ({ terms }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">Monthly Rent</p>
      <p className="text-lg font-semibold text-slate-900">
        {formatCurrency(terms.monthly_rent)}
      </p>
    </div>
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">Lease Term</p>
      <p className="text-sm font-medium text-slate-700">
        {formatDate(terms.lease_start_date)} - {formatDate(terms.lease_end_date)}
      </p>
    </div>
    {terms.escalation && (
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Escalation</p>
        <p className="text-sm font-medium text-slate-700">
          {terms.escalation.rate ? `${terms.escalation.rate}%` : terms.escalation.type} {terms.escalation.frequency}
        </p>
      </div>
    )}
    {terms.cam_charges && (
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">CAM Charges</p>
        <p className="text-sm font-medium text-slate-700">{formatCurrency(terms.cam_charges)}/mo</p>
      </div>
    )}
    {terms.property_address && (
      <div className="col-span-2">
        <p className="text-xs text-slate-500 uppercase tracking-wider">Property</p>
        <p className="text-sm font-medium text-slate-700">{terms.property_address}</p>
      </div>
    )}
    {terms.landlord_name && (
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Landlord</p>
        <p className="text-sm text-slate-700">{terms.landlord_name}</p>
      </div>
    )}
  </div>
);

// Insurance Terms Display
const InsuranceTermsDisplay: React.FC<{ terms: InsuranceTerms }> = ({ terms }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">BI Coverage Limit</p>
        <p className="text-lg font-semibold text-slate-900">
          {formatCurrency(terms.bi_coverage_limit)}
        </p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">BI Waiting Period</p>
        <p className="text-sm font-medium text-slate-700">
          {terms.bi_waiting_period_days ? `${terms.bi_waiting_period_days} days` : 'N/A'}
        </p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Period of Indemnity</p>
        <p className="text-sm font-medium text-slate-700">
          {terms.bi_period_of_indemnity_months ? `${terms.bi_period_of_indemnity_months} months` : 'N/A'}
        </p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">BI Deductible</p>
        <p className="text-sm font-medium text-slate-700">{formatCurrency(terms.bi_deductible)}</p>
      </div>
      {terms.carrier_name && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Carrier</p>
          <p className="text-sm text-slate-700">{terms.carrier_name}</p>
        </div>
      )}
      {terms.policy_number && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Policy #</p>
          <p className="text-sm text-slate-700">{terms.policy_number}</p>
        </div>
      )}
    </div>
    {terms.exclusions && terms.exclusions.length > 0 && (
      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
        <p className="text-xs text-amber-700 font-semibold mb-1">⚠️ Policy Exclusions</p>
        <ul className="text-sm text-amber-800 space-y-0.5">
          {terms.exclusions.slice(0, 5).map((exc, i) => (
            <li key={i}>• {exc}</li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

// Loan Terms Display
const LoanTermsDisplay: React.FC<{ terms: LoanTerms }> = ({ terms }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Monthly Payment</p>
        <p className="text-lg font-semibold text-slate-900">
          {formatCurrency(terms.monthly_payment)}
        </p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Interest Rate</p>
        <p className="text-sm font-medium text-slate-700">{formatPercent(terms.interest_rate)}</p>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider">Original Principal</p>
        <p className="text-sm font-medium text-slate-700">{formatCurrency(terms.original_principal)}</p>
      </div>
      {terms.current_balance && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Current Balance</p>
          <p className="text-sm font-medium text-slate-700">{formatCurrency(terms.current_balance)}</p>
        </div>
      )}
      {terms.loan_maturity_date && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Maturity Date</p>
          <p className="text-sm text-slate-700">{formatDate(terms.loan_maturity_date)}</p>
        </div>
      )}
      {terms.lender_name && (
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Lender</p>
          <p className="text-sm text-slate-700">{terms.lender_name}</p>
        </div>
      )}
    </div>
    {(terms.is_sba_loan || terms.ppp_loan || terms.eidl_loan) && (
      <div className="flex flex-wrap gap-2">
        {terms.is_sba_loan && (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">SBA Loan</span>
        )}
        {terms.ppp_loan && (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">PPP Loan</span>
        )}
        {terms.eidl_loan && (
          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">EIDL Loan</span>
        )}
      </div>
    )}
  </div>
);

// Single Document Card
const DocumentCard: React.FC<{
  doc: ExtractedDocument;
  expanded: boolean;
  onToggle: () => void;
}> = ({ doc, expanded, onToggle }) => {
  const config = DOC_CONFIG[doc.document_type] || DOC_CONFIG.contract;
  const Icon = config.icon;
  
  const getTermsDisplay = () => {
    switch (doc.document_type) {
      case 'lease_agreement':
        return doc.lease_terms ? <LeaseTermsDisplay terms={doc.lease_terms} /> : null;
      case 'insurance_policy':
        return doc.insurance_terms ? <InsuranceTermsDisplay terms={doc.insurance_terms} /> : null;
      case 'loan_document':
        return doc.loan_terms ? <LoanTermsDisplay terms={doc.loan_terms} /> : null;
      default:
        return null;
    }
  };
  
  const getKeyValue = () => {
    switch (doc.document_type) {
      case 'lease_agreement':
        return doc.lease_terms?.monthly_rent ? formatCurrency(doc.lease_terms.monthly_rent) + '/mo' : null;
      case 'insurance_policy':
        return doc.insurance_terms?.bi_coverage_limit ? formatCurrency(doc.insurance_terms.bi_coverage_limit) : null;
      case 'loan_document':
        return doc.loan_terms?.monthly_payment ? formatCurrency(doc.loan_terms.monthly_payment) + '/mo' : null;
      default:
        return null;
    }
  };

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900">{config.label}</span>
              <ConfidenceBadge confidence={doc.confidence} score={doc.confidence_score} />
              {doc.needs_review && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 bg-amber-100">
                  <AlertCircle className="w-3 h-3" />
                  Needs Review
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              {doc.original_filename || 'Document'}
              {getKeyValue() && <span className="ml-2 font-medium text-slate-700">• {getKeyValue()}</span>}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 pt-0 border-t border-inherit">
          {getTermsDisplay()}
          
          {/* Warnings */}
          {doc.extraction_warnings && doc.extraction_warnings.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs font-semibold text-amber-700 mb-1">Extraction Notes</p>
              <ul className="text-sm text-amber-800 space-y-0.5">
                {doc.extraction_warnings.map((warning, i) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Verification status */}
          {doc.verified_by && (
            <p className="mt-3 text-xs text-slate-500">
              ✓ Verified by {doc.verified_by} on {formatDate(doc.verified_at)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
export default function ExtractedDocumentsPanel({
  caseId,
  compact = false,
  onExtract
}: ExtractedDocumentsPanelProps) {
  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);

  const fetchDocuments = async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.get<DocumentSummary>(`/api/v1/supporting-documents/case/${caseId}`);
      setSummary(data);
      // Auto-expand first document if only one
      if (data.documents.length === 1) {
        setExpandedDoc(data.documents[0].document_id);
      }
    } catch (err: any) {
      console.error('Error fetching extracted documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleExtractAll = async () => {
    setExtracting(true);
    try {
      await api.post(`/api/v1/supporting-documents/case/${caseId}/extract-all`, {});
      await fetchDocuments();
      onExtract?.();
    } catch (err: any) {
      setError(err.message || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [caseId]);

  if (loading) {
    return (
      <div className="p-4 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading supporting documents...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg border border-red-200 bg-red-50">
        <div className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
        <button
          onClick={fetchDocuments}
          className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.documents.length === 0) {
    return (
      <div className="p-6 rounded-lg border border-slate-200 bg-slate-50 text-center">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-slate-500 text-sm">No extracted supporting documents</p>
        {summary && summary.pending_extraction > 0 && (
          <button
            onClick={handleExtractAll}
            disabled={extracting}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {extracting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            Extract {summary.pending_extraction} Documents
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="font-semibold text-slate-900">Supporting Documents</h3>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>{summary.extracted_documents} extracted</span>
            {summary.needs_review > 0 && (
              <span className="text-amber-600">• {summary.needs_review} need review</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {summary.pending_extraction > 0 && (
            <button
              onClick={handleExtractAll}
              disabled={extracting}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {extracting ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Eye className="w-3 h-3" />
              )}
              Extract ({summary.pending_extraction})
            </button>
          )}
          <button
            onClick={fetchDocuments}
            className="p-1.5 text-slate-400 hover:text-slate-600"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Document Cards */}
      <div className="space-y-3">
        {summary.documents.map((doc) => (
          <DocumentCard
            key={doc.document_id}
            doc={doc}
            expanded={expandedDoc === doc.document_id}
            onToggle={() => setExpandedDoc(
              expandedDoc === doc.document_id ? null : doc.document_id
            )}
          />
        ))}
      </div>
    </div>
  );
}
